/* Tradução mecânica; textos legais canônicos em pt-BR. Revisão jurídica por jurisdição não coberta nesta tradução. */
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Trash2, Mail } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import BrandLogo from '../../components/BrandLogo'

export default function DataDeletion() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.buttons.back')}
          </button>
          <BrandLogo className="h-8 w-auto object-contain" alt="Automais" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">

          <div className="flex items-center gap-3 mb-2">
            <Trash2 className="w-7 h-7 text-primary-600 shrink-0" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('legal.dataDeletion.title')}</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">{t('legal.dataDeletion.lastUpdate')}</p>

          <div className="prose prose-gray max-w-none space-y-8 text-gray-700">

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.rightTitle')}</h2>
              <p>
                <Trans i18nKey="legal.dataDeletion.rightBody" components={{ b: <strong /> }} />
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.whatDeletedTitle')}</h2>
              <p>{t('legal.dataDeletion.whatDeletedIntro')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>{t('legal.dataDeletion.whatDeletedItem1')}</li>
                <li>{t('legal.dataDeletion.whatDeletedItem2')}</li>
                <li>{t('legal.dataDeletion.whatDeletedItem3')}</li>
                <li>{t('legal.dataDeletion.whatDeletedItem4')}</li>
                <li>{t('legal.dataDeletion.whatDeletedItem5')}</li>
                <li>{t('legal.dataDeletion.whatDeletedItem6')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.whatRetainedTitle')}</h2>
              <p>{t('legal.dataDeletion.whatRetainedIntro')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>{t('legal.dataDeletion.whatRetainedItem1')}</li>
                <li>{t('legal.dataDeletion.whatRetainedItem2')}</li>
                <li>{t('legal.dataDeletion.whatRetainedItem3')}</li>
              </ul>
              <p className="mt-2">{t('legal.dataDeletion.whatRetainedFinal')}</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.howTitle')}</h2>

              <div className="rounded-xl border border-gray-200 p-5 mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('legal.dataDeletion.option1Title')}</h3>
                <ol className="list-decimal pl-6 space-y-1 text-sm">
                  <li><Trans i18nKey="legal.dataDeletion.option1Step1" components={{ b: <strong /> }} /></li>
                  <li><Trans i18nKey="legal.dataDeletion.option1Step2" components={{ b: <strong /> }} /></li>
                  <li><Trans i18nKey="legal.dataDeletion.option1Step3" components={{ b: <strong /> }} /></li>
                </ol>
                <p className="mt-3 text-sm text-gray-500">
                  <Trans i18nKey="legal.dataDeletion.option1Note" components={{ b: <strong /> }} />
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-2">{t('legal.dataDeletion.option2Title')}</h3>
                <p className="text-sm mb-3">
                  <Trans
                    i18nKey="legal.dataDeletion.option2Intro"
                    components={{
                      mail: (
                        <a
                          href="mailto:privacidade@automais.io"
                          className="text-primary-600 hover:text-primary-700 underline font-medium"
                        />
                      ),
                      b: <strong />,
                    }}
                  />
                </p>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>{t('legal.dataDeletion.option2Item1')}</li>
                  <li>{t('legal.dataDeletion.option2Item2')}</li>
                  <li>{t('legal.dataDeletion.option2Item3')}</li>
                </ul>
                <p className="mt-3 text-sm text-gray-500">
                  <Trans i18nKey="legal.dataDeletion.option2Note" components={{ b: <strong /> }} />
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.verificationTitle')}</h2>
              <p>{t('legal.dataDeletion.verificationBody')}</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('legal.dataDeletion.questionsTitle')}</h2>
              <p>
                <Trans
                  i18nKey="legal.dataDeletion.questionsBody"
                  components={{
                    privacy: (
                      <a href="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline" />
                    ),
                  }}
                />
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-xl text-sm space-y-1 flex items-start gap-3">
                <Mail className="w-4 h-4 text-primary-600 mt-0.5 shrink-0" />
                <div>
                  <p>
                    <a
                      href="mailto:privacidade@automais.io"
                      className="text-primary-600 hover:text-primary-700 underline font-medium"
                    >
                      privacidade@automais.io
                    </a>
                  </p>
                  <p className="text-gray-500">{t('legal.dataDeletion.dpoCaption')}</p>
                </div>
              </div>
            </section>

          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8 pb-6">
          {t('legal.footer', { year: new Date().getFullYear() })}
        </p>
      </main>
    </div>
  )
}
